import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Variable, BookOpen,
  Filter, HelpCircle, Search, Copy, Check, Plus,
  Folder, FileText, Globe
} from 'lucide-react';
import { useTemplateStore, sampleClauses, builtInFilters, syntaxHelp } from '../store/templateStore';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const { sidebarTab, setSidebarTab, detectedVariables } = useTemplateStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const tabs = [
    { id: 'variables' as const, icon: Variable, label: 'Variables' },
    { id: 'clauses' as const, icon: BookOpen, label: 'Clauses' },
    { id: 'filters' as const, icon: Filter, label: 'Filters' },
    { id: 'help' as const, icon: HelpCircle, label: 'Help' },
  ];

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter clauses by search
  const filteredClauses = sampleClauses.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group clauses by category
  const clausesByCategory = filteredClauses.reduce((acc, clause) => {
    if (!acc[clause.category]) acc[clause.category] = [];
    acc[clause.category].push(clause);
    return acc;
  }, {} as Record<string, typeof sampleClauses>);

  return (
    <>
      {/* Toggle button when closed */}
      {!isOpen && (
        <button
          onClick={onToggle}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-primary-800 border border-primary-700 rounded-l-lg hover:bg-primary-700 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-primary-400" />
        </button>
      )}
      
      {/* Sidebar panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full border-l border-primary-700/50 bg-primary-900/50 backdrop-blur-sm flex flex-col overflow-hidden"
          >
            {/* Tab buttons */}
            <div className="flex border-b border-primary-700/50">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setSidebarTab(tab.id)}
                  className={`
                    flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-all
                    ${sidebarTab === tab.id
                      ? 'text-accent-400 bg-white/5'
                      : 'text-primary-400 hover:text-primary-200 hover:bg-white/3'
                    }
                  `}
                  title={tab.label}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
              
              {/* Close button */}
              <button
                onClick={onToggle}
                className="px-3 text-primary-400 hover:text-white hover:bg-white/5 transition-colors"
                title="Close sidebar"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {/* Tab content */}
            <div className="flex-1 overflow-hidden">
              <AnimatePresence mode="wait">
                {/* Variables tab */}
                {sidebarTab === 'variables' && (
                  <motion.div
                    key="variables"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="h-full flex flex-col"
                  >
                    <div className="p-4 border-b border-primary-700/50">
                      <h3 className="text-sm font-medium text-white mb-2">Detected Variables</h3>
                      <p className="text-xs text-primary-400">
                        Variables found in your template
                      </p>
                    </div>
                    
                    <div className="flex-1 overflow-auto p-4">
                      {detectedVariables.length > 0 ? (
                        <div className="space-y-2">
                          {detectedVariables.map((v, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between p-2 bg-primary-800/50 rounded-lg group"
                            >
                              <div className="flex items-center gap-2">
                                <code className="text-xs text-accent-400 font-mono">
                                  {v.path}
                                </code>
                                <span className="badge badge-accent text-xs">
                                  {v.type}
                                </span>
                              </div>
                              <button
                                onClick={() => copyToClipboard(`{{${v.path}}}`, v.path)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-all"
                              >
                                {copiedId === v.path ? (
                                  <Check className="w-3 h-3 text-success-500" />
                                ) : (
                                  <Copy className="w-3 h-3 text-primary-400" />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-primary-400">
                          <Variable className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No variables detected yet</p>
                          <p className="text-xs mt-1">
                            Start typing {'{{variable}}'} in your template
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
                
                {/* Clauses tab */}
                {sidebarTab === 'clauses' && (
                  <motion.div
                    key="clauses"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="h-full flex flex-col"
                  >
                    <div className="p-4 border-b border-primary-700/50">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search clauses..."
                          className="w-full pl-9 pr-4 py-2 bg-primary-800/50 border border-primary-600/50 rounded-lg text-sm text-white placeholder-primary-400 focus:outline-none focus:border-accent-500/50"
                        />
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-auto p-4">
                      <div className="space-y-4">
                        {Object.entries(clausesByCategory).map(([category, clauses]) => (
                          <div key={category}>
                            <div className="flex items-center gap-2 mb-2">
                              <Folder className="w-4 h-4 text-primary-400" />
                              <span className="text-xs font-medium text-primary-300 uppercase tracking-wider">
                                {category}
                              </span>
                            </div>
                            <div className="space-y-1 ml-6">
                              {clauses.map(clause => (
                                <div
                                  key={clause.id}
                                  className="flex items-center justify-between p-2 bg-primary-800/30 rounded-lg group hover:bg-primary-800/50 transition-colors cursor-pointer"
                                  onClick={() => copyToClipboard(`{{@clause:${clause.id}}}`, clause.id)}
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <FileText className="w-3 h-3 text-primary-400" />
                                      <span className="text-sm text-white truncate">
                                        {clause.name}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 mt-1">
                                      {clause.jurisdictions.map(j => (
                                        <span key={j} className="badge badge-accent text-xs">
                                          {j}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                    {copiedId === clause.id ? (
                                      <Check className="w-4 h-4 text-success-500" />
                                    ) : (
                                      <Copy className="w-4 h-4 text-primary-400" />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {/* Filters tab */}
                {sidebarTab === 'filters' && (
                  <motion.div
                    key="filters"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="h-full overflow-auto p-4"
                  >
                    <div className="space-y-3">
                      {builtInFilters.map(filter => (
                        <div
                          key={filter.name}
                          className="p-3 bg-primary-800/30 rounded-lg group hover:bg-primary-800/50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <code className="text-sm font-mono text-accent-400">
                              | {filter.name}
                            </code>
                            <button
                              onClick={() => copyToClipboard(`| ${filter.name}`, filter.name)}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-all"
                            >
                              {copiedId === filter.name ? (
                                <Check className="w-3 h-3 text-success-500" />
                              ) : (
                                <Copy className="w-3 h-3 text-primary-400" />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-primary-300 mb-2">
                            {filter.description}
                          </p>
                          <code className="text-xs text-primary-400 font-mono block bg-primary-900/50 p-2 rounded">
                            {filter.example}
                          </code>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
                
                {/* Help tab */}
                {sidebarTab === 'help' && (
                  <motion.div
                    key="help"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="h-full overflow-auto p-4"
                  >
                    <div className="space-y-6">
                      {syntaxHelp.map(section => (
                        <div key={section.category}>
                          <h4 className="text-sm font-medium text-white mb-3">
                            {section.category}
                          </h4>
                          <div className="space-y-2">
                            {section.items.map((item, i) => (
                              <div
                                key={i}
                                className="p-2 bg-primary-800/30 rounded-lg group hover:bg-primary-800/50 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <code className="text-xs font-mono text-accent-400">
                                    {item.syntax}
                                  </code>
                                  <button
                                    onClick={() => copyToClipboard(item.syntax, `help-${i}`)}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-all"
                                  >
                                    {copiedId === `help-${i}` ? (
                                      <Check className="w-3 h-3 text-success-500" />
                                    ) : (
                                      <Copy className="w-3 h-3 text-primary-400" />
                                    )}
                                  </button>
                                </div>
                                <p className="text-xs text-primary-400 mt-1">
                                  {item.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
